// Canonical identity only: display names and announcement prose are not identity.
export const dojoToastIdentityId = value => {
  if (value && typeof value === 'object') return dojoToastIdentityId(value._id || value.id || value.user_id);
  return value === null || value === undefined ? '' : String(value);
};

// Own completion has one announcement cue: the top-bar activity shimmer.
// Unknown viewer/actor identity cannot establish a peer. The toast owner defers
// events during profile hydration and re-evaluates them once identity is known.
export function shouldShowDojoToast(incoming, viewer) {
  if (incoming.event !== 'completed') return true;
  const viewerId = dojoToastIdentityId(viewer);
  const actorId = dojoToastIdentityId(incoming.user);
  const ownerId = dojoToastIdentityId(incoming.ownerId) || actorId;
  return Boolean(viewerId && ownerId && ownerId !== viewerId && actorId !== viewerId);
}

// History outlives rendered pills, so a replay after the exit cannot revive it.
// Priority 1 is the announcement; a late raw update must never downgrade it.
export function acceptDojoToastEvent(history, incoming) {
  if (incoming.event === 'started' && history.has(`${incoming.activityId}:completed`)) return false;
  const key = `${incoming.activityId}:${incoming.event}`;
  const prior = history.get(key);
  const time = Date.parse(incoming.completionRevision) || 0;
  const serial = Number(incoming.completionTimeRevision || 0);
  const priority = Number(incoming.priority || 0);
  if (prior && (serial < prior.serial || (serial === prior.serial && (time < prior.time || (time === prior.time && priority <= prior.priority))))) return false;
  history.delete(key);
  history.set(key, { time, serial, priority });
  while (history.size > 500) history.delete(history.keys().next().value);
  return true;
}

export function mergeDojoToast(pills, incoming) {
  const index = pills.findIndex(p => String(p.activityId) === String(incoming.activityId));
  if (index < 0) return [...pills, incoming];
  const existing = pills[index];
  const next = existing.mediaFiles?.length && !incoming.mediaFiles?.length
    ? { ...incoming, mediaFiles: existing.mediaFiles } : incoming;
  return pills.map((p, i) => i === index ? next : p);
}

const labels = { workout: 'a workout', meal: 'a meal', media: 'a progress photo', metric: 'body metrics', metrics: 'body metrics', survey: 'a check-in' };

// Only server-authorized, complete occurrence snapshots can change the copy.
// Membership is read at event handling time, never captured by the WS closure.
export function dojoCompletionCopy(event, { inDojo, dojoId, currentUserId }) {
  const name = String(event.user_id) === String(currentUserId) ? 'You' : (event.user_name || 'A dojo member');
  const fallback = { message: `${name} completed ${labels[event.activity_type] || 'an activity'}`, coachHighlight: event.announcement || null, contextual: false };
  const p = event.completion_progress;
  if (inDojo !== true || !dojoId || !p || String(p.dojo_id) !== String(dojoId)
      || String(p.activity_id) !== String(event.activity_id) || p.activity_type !== event.activity_type
      || !p.occurrence_key || !p.completion_revision || p.completion_revision !== event.completion_revision
      || !Number.isInteger(p.eligible_count) || !Number.isInteger(p.completed_count)
      || p.eligible_count < 1 || p.eligible_count > 100 || p.completed_count < 1 || p.completed_count > p.eligible_count
      || typeof p.activity_name !== 'string' || !p.activity_name.trim()) return fallback;
  const activityName = p.activity_name.trim().slice(0, 160);
  const count = `${p.completed_count} of ${p.eligible_count}`;
  if (p.eligible_count === 1) return { message: `${name} completed ${activityName}`, coachHighlight: 'Dojo activity complete. Well done!', contextual: true };
  if (p.completed_count === p.eligible_count) return { message: `Everyone completed ${activityName}!`, coachHighlight: `${name} finished · ${count} complete. Great work, everyone!`, contextual: true };
  if (p.completed_count === 1) return { message: `${name} finished ${activityName} first`, coachHighlight: `First in the Dojo · ${count} complete`, contextual: true };
  return { message: `${name} completed ${activityName}`, coachHighlight: `Dojo progress · ${count} complete`, contextual: true };
}
